import { ROUTE_CONFIG } from "@/routes/config";

import { UnipileConnectType } from "@/pages/Private/pages/Account/hooks/useUnipileConnect";
import { OnboardingTourReadiness, TourStepId } from "@/pages/Private/redux/onboardingTour/schema";

import { DashboardFirstSteps } from "../schema/dashboard";

/** Stable row ids of the dashboard onboarding checklist, in display order. */
export type OnboardingItemKey =
	| "createLists"
	| "profileTracker"
	| "leadScoring"
	| "connectSenders"
	| "deepResearch"
	| "knowledge"
	| "firstCampaign"
	| "reachOut"
	| "crm";

/** Status pill on the Deep-Research row while its generation runs / finishes. */
export type OnboardingItemPill = "generating" | "resultReady";

export interface FirstStepItem {
	key: OnboardingItemKey;
	title: string;
	actionLabel: string;
	/** Navigation target; for tour rows only used as the "Learn more" modal fallback. */
	to: string;
	done: boolean;
	/** Topic id into `LEARN_CONTENT` for this step's "Learn more" modal ("" = none). */
	learnTopic: string;
	/** When set, the CTA triggers the in-app Unipile connect flow instead of navigating. */
	connect?: UnipileConnectType;
	/** When set, the CTA opens the setup-guide tour (PREC-3700) at this step. */
	tourStep?: TourStepId;
	/** Deep-Research generation status pill (only on the deepResearch row). */
	pill?: OnboardingItemPill;
}

/** Tour-derived inputs the checklist merges with the dashboard first-steps flags. */
export interface FirstStepsTourContext {
	/** Steps already completed in the setup-guide tour. */
	completedSteps: TourStepId[];
	readiness: OnboardingTourReadiness | null;
	/** i18n translate function — row labels live under `onboardingTour.dashboard.rows`. */
	t: (key: string) => string;
}

interface StepConfig {
	key: OnboardingItemKey;
	to: string;
	learnTopic: string;
	tourStep?: TourStepId;
	/** First-steps-flag part of done-ness; tour rows additionally check completedSteps. */
	isDone: (firstSteps: DashboardFirstSteps) => boolean;
	/** Done-ness of rows whose work happens in the background, read from readiness. */
	isDoneFromReadiness?: (readiness: OnboardingTourReadiness) => boolean;
	/**
	 * When true, this row is done ONLY when its first-steps flag says so — the
	 * "completed tour step" shortcut is ignored. Lead scoring uses this: the
	 * scoring is generated pre-activated now, but the row must still reflect the
	 * backend-verified activation flag rather than any tour progress.
	 */
	requiresRealDone?: boolean;
}

const STEPS: StepConfig[] = [
	{
		key: "createLists",
		to: ROUTE_CONFIG.NEW_LIST,
		learnTopic: "first-list",
		isDone: (firstSteps) => firstSteps.hasCreatedOneList,
	},
	{
		key: "profileTracker",
		to: ROUTE_CONFIG.PROFILE_TRACKER,
		learnTopic: "profile-tracker",
		isDone: (firstSteps) => firstSteps.hasCreatedOneProfileTrackerList,
	},
	{
		key: "leadScoring",
		to: ROUTE_CONFIG.LEAD_SCORING,
		learnTopic: "lead-scoring",
		// The scoring is reviewed in the tour again, so the row opens it there —
		// but it still closes only on the backend-verified activation flag.
		tourStep: TourStepId.SCORING_INTRO,
		isDone: (firstSteps) => firstSteps.hasReviewedAndActivatedTheLeadScoring,
		requiresRealDone: true,
	},
	{
		key: "connectSenders",
		to: ROUTE_CONFIG.ACCOUNT,
		learnTopic: "connect-accounts",
		tourStep: TourStepId.CONNECT,
		isDone: (firstSteps) =>
			firstSteps.hasConnectedLinkedinProfile || firstSteps.hasConnectedOneEMail,
	},
	{
		key: "deepResearch",
		to: `${ROUTE_CONFIG.TEMPLATES}?tab=DEEP_RESEARCH_PROMPTS`,
		learnTopic: "deep-research-prompt",
		isDone: (firstSteps) => firstSteps.hasReviewedTheDeepResearchPrompt,
	},
	{
		key: "knowledge",
		to: ROUTE_CONFIG.KNOWLEDGE_DB,
		learnTopic: "",
		// No dashboard first-steps flag exists — the Knowledge Base is built in the
		// background, so the row closes once it actually has content.
		isDone: () => false,
		isDoneFromReadiness: (readiness) => readiness.knowledgeDbReady,
	},
	{
		key: "firstCampaign",
		to: ROUTE_CONFIG.LISTS,
		learnTopic: "first-campaign",
		isDone: (firstSteps) => firstSteps.hasCreatedFirstCampaign,
	},
	{
		key: "reachOut",
		to: ROUTE_CONFIG.LISTS,
		learnTopic: "",
		// Closes on the first step that actually went out — a campaign step or a
		// manual chat message; scheduled-but-unsent steps don't count.
		isDone: (firstSteps) => firstSteps.hasReachedOutToFirstLeads === true,
	},
	{
		key: "crm",
		to: ROUTE_CONFIG.INTEGRATIONS,
		learnTopic: "connect-crm",
		isDone: (firstSteps) => firstSteps.hasConnectedSignalistToOneCrm,
	},
];

/**
 * Where the lead-scoring row reopens the guide (PREC-3861). Someone who already
 * walked the review screens wants the last one — "Check & activate", the screen
 * that shows their own leads scored — not the explainer they have seen four
 * times. So the row lands on the check as soon as everything before it is done.
 */
const leadScoringTourStep = (completedSteps: TourStepId[]): TourStepId =>
	completedSteps.includes(TourStepId.SCORING_GRADING)
		? TourStepId.SCORING_ACTIVATE
		: TourStepId.SCORING_INTRO;

const deepResearchPill = (
	done: boolean,
	readiness: OnboardingTourReadiness | null
): OnboardingItemPill | undefined => {
	if (done || !readiness) {
		return undefined;
	}

	if (!readiness.deepResearchPromptReady) {
		return "generating";
	}

	return readiness.deepResearchResponseCount > 0 ? "resultReady" : undefined;
};

/**
 * Maps the dashboard first-steps flags + the setup-guide tour state to the
 * onboarding checklist rows. A row is done when any source says so — its
 * first-steps flag, a completed tour step, or (for rows whose work runs in the
 * background, like the Knowledge Base) the readiness facts — except rows flagged
 * `requiresRealDone` (lead scoring), which ignore the completed-tour-step
 * shortcut and rely solely on the backend-verified activation flag.
 */
export const firstStepsToItems = (
	firstSteps: DashboardFirstSteps,
	{ completedSteps, readiness, t }: FirstStepsTourContext
): FirstStepItem[] =>
	STEPS.map((step) => {
		const doneFromTour =
			!step.requiresRealDone &&
			step.tourStep !== undefined &&
			completedSteps.includes(step.tourStep);
		const doneFromReadiness =
			readiness !== null && (step.isDoneFromReadiness?.(readiness) ?? false);
		const done = step.isDone(firstSteps) || doneFromTour || doneFromReadiness;

		return {
			key: step.key,
			title: t(`onboardingTour.dashboard.rows.${step.key}.title`),
			actionLabel: t(`onboardingTour.dashboard.rows.${step.key}.action`),
			to: step.to,
			learnTopic: step.learnTopic,
			tourStep: step.key === "leadScoring" ? leadScoringTourStep(completedSteps) : step.tourStep,
			done,
			...(step.key === "deepResearch" ? { pill: deepResearchPill(done, readiness) } : {}),
		};
	});
